import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Link,
  Alert,
  AlertIcon,
  Divider,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import {
  FiSmartphone,
  FiMonitor,
  FiDownload,
  FiArrowRight,
  FiCheck,
  FiUsers,
  FiStar,
  FiGlobe,
  FiPlayCircle,
  FiArrowLeft,
} from "react-icons/fi";
import { FaGooglePlay, FaApple } from "react-icons/fa";
import Icon from "components/icons/Icon";

function TrainerGuide() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get trainer info from navigation state
  const trainerDetails = location.state?.trainerDetails || {};
  const trainerName = trainerDetails.name || "Trainer";
  const trainerEmail = trainerDetails.email || "";
  const agencyCode = trainerDetails.agencyCode || "Your Agency";
  
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const bgColor = useColorModeValue('white', 'gray.800');
  const brandColor = useColorModeValue("brand.500", "brand.400");

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      pt={{ base: "20px", md: "40px" }}
      pb={{ base: "40px", md: "60px" }}
      px={{ base: '20px', md: '40px' }}>

      <VStack spacing={8} maxW="900px" mx="auto">
        {/* Header */}
        <Box textAlign='center'>
          <Icon as={FiUsers} boxSize={16} color="brand.500" mb={4} />
          <Heading color={textColor} fontSize={{ base: '2xl', md: '3xl' }} mb={2}>
            🎉 Welcome to TrackHQ, {trainerName}!
          </Heading>
          <Text color={textColorSecondary} fontSize={{ base: 'md', md: 'lg' }}>
            Your trainer account has been created successfully
          </Text>
          <Text color="brand.500" fontSize="sm" fontWeight="500" mt={2}>
            Linked to: {agencyCode}
          </Text>
        </Box>

        {/* Success Alert */}
        <Alert status="success" borderRadius="lg" bg="green.50" border="1px solid" borderColor="green.200">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontSize="md" fontWeight="600" color="green.700">
              Account Created Successfully!
            </Text>
            <Text fontSize="sm" color="green.600">
              Your complete trainer profile is ready. Choose how you'd like to access TrackHQ below.
            </Text>
          </VStack>
        </Alert>

        {/* Access Options */}
        <Box w="100%">
          <Heading size="lg" color={textColor} mb={6} textAlign="center">
            Choose Your Access Method
          </Heading>
          
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {/* Mobile App - iOS */}
            <Card 
              borderRadius="20px" 
              boxShadow="lg" 
              bg={bgColor}
              border="2px solid"
              borderColor="transparent"
              _hover={{ 
                borderColor: brandColor,
                transform: "translateY(-4px)",
                boxShadow: "xl"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={6} textAlign="center">
                <Icon as={FaApple} boxSize={12} color="gray.600" mb={4} />
                <Heading size="md" color={textColor} mb={3}>
                  iOS App
                </Heading>
                <Badge colorScheme="blue" mb={3}>Recommended</Badge>
                <Text color={textColorSecondary} fontSize="sm" mb={4}>
                  Full-featured mobile experience for iPhone and iPad
                </Text>
                
                <VStack spacing={2} mb={4}>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Client management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Group management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Workout management</Text>
                  </HStack>
                </VStack>

                <Button
                  colorScheme="gray"
                  size="lg"
                  w="100%"
                  leftIcon={<Icon as={FiDownload} />}
                  as={Link}
                  href="https://testflight.apple.com/join/zcDDnu1B"
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  Download iOS App
                </Button>
              </CardBody>
            </Card>

            {/* Mobile App - Android */}
            <Card 
              borderRadius="20px" 
              boxShadow="lg" 
              bg={bgColor}
              border="2px solid"
              borderColor="transparent"
              _hover={{ 
                borderColor: brandColor,
                transform: "translateY(-4px)",
                boxShadow: "xl"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={6} textAlign="center">
                <Icon as={FaGooglePlay} boxSize={12} color="green.500" mb={4} />
                <Heading size="md" color={textColor} mb={3}>
                  Android App
                </Heading>
                <Badge colorScheme="green" mb={3}>Recommended</Badge>
                <Text color={textColorSecondary} fontSize="sm" mb={4}>
                  Full-featured mobile experience for Android devices
                </Text>
                
                <VStack spacing={2} mb={4}>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Client management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Group management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Workout management</Text>
                  </HStack>
                </VStack>

                <Button
                  colorScheme="green"
                  size="lg"
                  w="100%"
                  leftIcon={<Icon as={FiDownload} />}
                  as={Link}
                  href="https://play.google.com/store/apps/details?id=com.speeir.trackhq"
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  Download Android App
                </Button>
              </CardBody>
            </Card>

            {/* Web App */}
            <Card 
              borderRadius="20px" 
              boxShadow="lg" 
              bg={bgColor}
              border="2px solid"
              borderColor="transparent"
              _hover={{ 
                borderColor: brandColor,
                transform: "translateY(-4px)",
                boxShadow: "xl"
              }}
              transition="all 0.3s ease"
            >
              <CardBody p={6} textAlign="center">
                <Icon as={FiMonitor} boxSize={12} color="blue.500" mb={4} />
                <Heading size="md" color={textColor} mb={3}>
                  Web App
                </Heading>
                <Badge colorScheme="purple" mb={3}>Desktop Friendly</Badge>
                <Text color={textColorSecondary} fontSize="sm" mb={4}>
                  Access TrackHQ directly from your web browser
                </Text>
                
                 <VStack spacing={2} mb={4}>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Client management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Group management</Text>
                  </HStack>
                  <HStack fontSize="xs" color="green.600">
                    <Icon as={FiCheck} color="green.500" boxSize={3} />
                    <Text>Workout management</Text>
                  </HStack>
                </VStack>

                <Button
                  colorScheme="brand"
                  size="lg"
                  w="100%"
                  leftIcon={<Icon as={FiGlobe} />}
                  as={Link}
                  href="https://trackhq.speeir.com"
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  Access Web App
                </Button>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>

        {/* Getting Started Guide */}
        <Card w="100%" borderRadius="20px" boxShadow="lg" bg={bgColor}>
          <CardBody p={8}>
            <Heading size="lg" color={textColor} mb={6} textAlign="center">
              Getting Started as a Trainer
            </Heading>
            
            <VStack align="start" spacing={4}>
              <Text color={textColorSecondary} fontSize="md" textAlign="center" w="100%" mb={4}>
                TrackHQ provides a comprehensive platform for managing your fitness training business. 
                Access all features through mobile apps (iOS/Android) or web dashboard.
              </Text>
              
              <Heading size="md" color={brandColor} mb={2}>
                � Core Platform Features
              </Heading>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                <List spacing={3}>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Client Management:</strong> View all clients, track relationships, and detailed profiles</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Group Management:</strong> Create training groups and assign workouts</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Workout Creation:</strong> Build custom workout plans for group/individuals</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Workout Calendar:</strong> Schedule and manage client workout plans</Text>
                  </ListItem>
                </List>
                
                <List spacing={3}>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Exercise Library:</strong> Access comprehensive exercise database</Text>
                  </ListItem>
                  <ListItem display="flex" alignItems="center">
                    <Icon as={FiCheck} color="green.500" boxSize={4} mr={3} />
                    <Text><strong>Exercise Management:</strong> Create, update, and organize exercises</Text>
                  </ListItem>
                </List>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Login Instructions */}
        <Card w="100%" borderRadius="20px" boxShadow="lg" bg="blue.50" border="1px solid" borderColor="blue.200">
          <CardBody p={6}>
            <VStack spacing={4}>
              <Heading size="md" color="blue.700" textAlign="center">
                🔐 Your Login Credentials
              </Heading>
              
              <VStack spacing={2} textAlign="center">
                <Text color="blue.600" fontSize="sm">
                  <strong>Email:</strong> {trainerEmail}
                </Text>
                <Text color="blue.600" fontSize="sm">
                  <strong>Password:</strong> The password you just created
                </Text>
              </VStack>

              <Alert status="info" bg="blue.100" border="1px solid" borderColor="blue.300">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="600" color="blue.700">
                    Important: Save Your Credentials
                  </Text>
                  <Text fontSize="xs" color="blue.600">
                    Please save your login credentials safely. You'll need them to access TrackHQ on any device.
                  </Text>
                </VStack>
              </Alert>
            </VStack>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <VStack spacing={4} w="100%">
          <Button
            size="lg"
            colorScheme="brand"
            rightIcon={<Icon as={FiArrowRight} />}
            onClick={() => navigate('/auth/sign-in', { 
              state: { 
                message: "Welcome back! Sign in to access your trainer dashboard",
                email: trainerEmail 
              } 
            })}
            w={{ base: "100%", md: "auto" }}
            px={8}
          >
            Continue to Sign In
          </Button>

          <Button
            variant="ghost"
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={() => navigate('/auth/sign-in')}
            color={textColorSecondary}
          >
            Back to Sign In
          </Button>
        </VStack>

        {/* Footer */}
        <Box textAlign="center" pt={8} borderTop="1px solid" borderColor="gray.200" w="100%">
          <Text fontSize="xs" color={textColorSecondary}>
            Need help? Contact support team.
          </Text>
          <Text fontSize="xs" color={textColorSecondary} mt={2}>
            Powered by{' '}
            <Link color="brand.500" href="https://www.speeir.com" isExternal>
              Speeir Limited
            </Link>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
}

export default TrainerGuide;